import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { extractZip } from '../../../util/usZip';

import DeliveryRangeCheck from './DeliveryRangeCheck';
import css from './SectionFulfillmentOptions.module.css';

const SectionFulfillmentOptions = ({
  listing,
  className = '',
  selectedOption: externalSelectedOption,
  onSelectOption: externalOnSelectOption,
  onDeliveryAddressChange,
  // When true, the delivery address field is hidden at <1024px and a
  // short helper text is shown instead. The address is collected inside
  // the OrderPanel modal on mobile (see ListingPageCarousel mobileExtrasContent).
  // Desktop always shows the full address field inline.
  addressFieldHiddenOnMobile = false,
}) => {
  const publicData = listing?.attributes?.publicData || {};
  const geolocation = listing?.attributes?.geolocation;

  const pickupDelivery = publicData.fulfillment || [];
  const hasPickup = pickupDelivery.includes('pickup');
  const hasDelivery = pickupDelivery.includes('delivery');
  const hasSetup = pickupDelivery.includes('setup');
  const parsePositiveNumber = raw => {
    if (raw == null || raw === '') return null;
    const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const deliveryRadiusMiles = parsePositiveNumber(publicData.delivery_radius);
  const deliveryFee = parsePositiveNumber(publicData.delivery_fee);
  const setupRadiusMiles = parsePositiveNumber(publicData.setup_radius);
  const setupFee = parsePositiveNumber(publicData.setup_fee);

  const hasMultipleOptions =
    [hasPickup, hasDelivery, hasSetup].filter(Boolean).length > 1;

  const defaultOption = hasPickup ? 'pickup' : hasDelivery ? 'delivery' : 'setup';
  const [internalOption, setInternalOption] = useState(defaultOption);
  const isExternal =
    externalSelectedOption !== undefined && externalOnSelectOption !== undefined;
  const selectedOption = isExternal
    ? externalSelectedOption || defaultOption
    : internalOption;
  const setSelectedOption = isExternal ? externalOnSelectOption : setInternalOption;

  // If parent hasn't initialized external state yet, seed it with the default.
  useEffect(() => {
    if (isExternal && externalSelectedOption == null) {
      externalOnSelectOption(defaultOption);
    }
  }, [isExternal]); // eslint-disable-line

  // Don't render if no fulfillment options
  if (!hasPickup && !hasDelivery && !hasSetup) {
    return null;
  }

  const classes = classNames(css.root, className);
  const needsAddress = selectedOption === 'delivery' || selectedOption === 'setup';
  const activeRadius =
    selectedOption === 'setup' ? setupRadiusMiles : deliveryRadiusMiles;
  const addressLabel =
    selectedOption === 'setup' ? 'Setup address' : 'Delivery address';
  const addressHelper =
    selectedOption === 'setup'
      ? 'Address where the kit will be delivered and set up'
      : 'Address where the kit will be delivered';
  // Host ZIP fallback for the range check when the listing has no
  // geolocation (the centroid table resolves it from this).
  const listingZip = extractZip(publicData.location?.address);

  return (
    <section className={classes}>
      <h3 className={css.title}>How do you want to receive this kit?</h3>

      <div className={css.optionSelector}>
        {hasPickup && (
          <button
            type="button"
            className={`${css.optionCard} ${selectedOption === 'pickup' ? css.selected : ''}`}
            onClick={() => setSelectedOption('pickup')}
          >
            <div className={css.optionEmoji}>📍</div>
            <div className={css.optionLabel}>Pickup</div>
            <div className={css.optionPrice}>Free</div>
          </button>
        )}
        {hasDelivery && (
          <button
            type="button"
            className={`${css.optionCard} ${selectedOption === 'delivery' ? css.selected : ''}`}
            onClick={() => setSelectedOption('delivery')}
          >
            <div className={css.optionEmoji}>🚚</div>
            <div className={css.optionLabel}>Delivery</div>
            <div className={css.optionPrice}>
              {deliveryFee ? `+$${deliveryFee}` : 'Quote at checkout'}
            </div>
          </button>
        )}
        {hasSetup && (
          <button
            type="button"
            className={`${css.optionCard} ${selectedOption === 'setup' ? css.selected : ''}`}
            onClick={() => setSelectedOption('setup')}
          >
            <div className={css.optionEmoji}>🛠</div>
            <div className={css.optionLabel}>Setup</div>
            <div className={css.optionPrice}>
              {setupFee ? `+$${setupFee}` : 'Quote at checkout'}
            </div>
          </button>
        )}
      </div>

      {selectedOption === 'setup' && (
        <p className={css.optionDescription}>
          Host delivers and sets up your kit on site, returns at end of event to break down.
        </p>
      )}

      {needsAddress && (
        addressFieldHiddenOnMobile ? (
          <>
            <div className={css.deliveryRangeCheckDesktopOnly}>
              <DeliveryRangeCheck
                listingGeolocation={geolocation}
                listingZip={listingZip}
                radiusMiles={activeRadius}
                onAddressChange={onDeliveryAddressChange}
                addressLabel={addressLabel}
                helperText={addressHelper}
              />
            </div>
            <p className={css.mobileAddressHelper}>
              We&rsquo;ll collect your address when you book.
            </p>
          </>
        ) : (
          <DeliveryRangeCheck
            listingGeolocation={geolocation}
            listingZip={listingZip}
            radiusMiles={activeRadius}
            onAddressChange={onDeliveryAddressChange}
            addressLabel={addressLabel}
            helperText={addressHelper}
          />
        )
      )}
    </section>
  );
};

export default SectionFulfillmentOptions;
